/* eslint-disable i18next/no-literal-string */
import { useMemo } from 'react';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
// import { onError } from '@apollo/client/link/error';
// import { InMemoryCache, ApolloClient, ApolloLink, HttpLink } from '@apollo/client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';

import fetch from 'isomorphic-unfetch';

import { isBrowser } from 'utils/misc-utils';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

import possibleTypes from 'shared/schemas/fragment-types';
import PublicEnv from 'shared/utils/public-env';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: possibleTypes,
});

let globalApolloClient = null;

let { consumerUrl } = PublicEnv;

if (isBrowser()) {
  consumerUrl = ``;
}
export const EXTENSION_ERRORS = {
  AUTHERROR: 'AUTHENTICATION_ERROR',
  SESSIONEXPIRED: 'SESSION_INVALID',
};

const handleSessionExpired = (error) => {
  if (isBrowser()) {
    console.error(error);
    // "Simply remove token when session expires. Redirects should be handled at the page level."
    setPersistedValue('access-token', false);
  }
};

const errorLinkFactory = ({ onSessionExpired, onAuthError } = {}) =>
  onError(({ graphQLErrors, networkError, operation }) => {
    // console.error(`Error in the following operation: ${operation}`);

    if (graphQLErrors) {
      graphQLErrors.map((error) => {
        console.error(error);
        if (error.extensions?.code === EXTENSION_ERRORS.AUTHERROR) {
          // console.error('Auth error');
          onAuthError?.call(this, error);
        }
        if (error.extensions?.code === EXTENSION_ERRORS.SESSIONEXPIRED) {
          // console.error('Session expired');
          onSessionExpired?.call(this, error);
        }
      });
    }

    if (networkError) {
      console.error(`Network Error: ${networkError.message}`);
    }
  });

function setAuthorization(operation, forward) {
  const headers = {};
  if (isBrowser()) {
    const token = getPersistedValue('access-token');
    headers.url = window.location.href;

    if (token) {
      headers.authorization = token;
    }
  }

  operation.setContext(() => ({
    headers: { ...operation.getContext().headers, ...headers },
  }));

  return forward(operation);
}

/**
 * Creates and configures the ApolloClient
 */
export function createApolloClient() {
  const name = `Marketplace (${PublicEnv.appEnv})`;

  const errorLink = errorLinkFactory({ onSessionExpired: handleSessionExpired, onAuthError: handleSessionExpired });
  const httpLink = new HttpLink({
    credentials: 'include',
    fetch,
    uri: `${consumerUrl}/graphql`,
    useGETForHashedQueries: true,
  });
  const batchHttpLink = new BatchHttpLink({
    fetch,
    headers: { batch: true },
    uri: `${consumerUrl}/graphql`,
  });
  return new ApolloClient({
    connectToDevTools: isBrowser(),
    ssrMode: !isBrowser(),
    name,
    version: PublicEnv.herokuSlugCommit,
    cache: new InMemoryCache({ fragmentMatcher }),
    // eslint-disable-next-line lodash/prefer-lodash-method
    link: ApolloLink.split(
      (operation) => operation.getContext().useBatch,
      ApolloLink.from([setAuthorization, /* errorLink, */ batchHttpLink]),
      ApolloLink.from([
        setAuthorization,
        errorLink,
        createPersistedQueryLink({ useGETForHashedQueries: true }),
        httpLink,
      ])
    ),
  });
}

export function initializeApollo(initialState = null) {
  const apolloClient = globalApolloClient || createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    // TODO: What does that mean? --^
    const existingCache = apolloClient.extract();
    // Restore the cache using the data passed from getStaticProps/getServerSideProps
    // combined with the existing cached data
    apolloClient.cache.restore({ ...existingCache, ...initialState });
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') {
    return apolloClient;
  }
  // Create the Apollo Client once in the client
  if (!globalApolloClient) {
    globalApolloClient = apolloClient;
  }
  return apolloClient;
}

export function useApollo(initialState) {
  const client = useMemo(() => initializeApollo(initialState), [initialState]);
  return client;
}
